// Shared types + presentation config for the unified benefits status ladder.
// Mirrors the backend BenefitsStatusService response (GET benefits/status/:id
// and POST benefits/status/batch).

export type BenefitsState =
  | 'ineligible'
  | 'missing_information'
  | 'ready_to_send'
  | 'sent'
  | 'ready_to_mail'
  | 'mailed'
  | 'completed';

export type FormLifecycle = {
  ready: boolean;
  sent: boolean;
  signed: boolean;
  sentAt: string | null;
  signedAt: string | null;
};

export interface BenefitsStatusResult {
  state: BenefitsState;
  benefitsType: 'cpp' | 'qpp';
  survivorApplicable: boolean;
  perForm: {
    death_benefit: FormLifecycle;
    survivor_pension: FormLifecycle;
  };
  mailingStatus: 'pending' | 'sent' | 'failed' | null;
  // Ineligible reason, or confirmation number when completed. Null otherwise.
  note?: string | null;
}

// lucide-react icon names are resolved in the badge component; here we keep the
// label + Tailwind chip classes so both the sidebar and the list cells render
// the same visual language.
export interface BenefitsStateDisplay {
  label: string;
  chipClass: string;
  icon: 'ban' | 'alert' | 'send' | 'mail' | 'check' | 'clock';
  iconClass: string;
}

export const BENEFITS_STATE_DISPLAY: Record<BenefitsState, BenefitsStateDisplay> = {
  ineligible: {
    label: 'Ineligible',
    chipClass: 'bg-orange-100 text-orange-700',
    icon: 'ban',
    iconClass: 'text-orange-500',
  },
  missing_information: {
    label: 'Missing info',
    chipClass: 'bg-red-100 text-red-700',
    icon: 'alert',
    iconClass: 'text-red-500',
  },
  ready_to_send: {
    label: 'Ready to send',
    chipClass: 'bg-blue-100 text-blue-700',
    icon: 'clock',
    iconClass: 'text-blue-500',
  },
  sent: {
    label: 'Sent to sign',
    chipClass: 'bg-amber-100 text-amber-700',
    icon: 'send',
    iconClass: 'text-amber-500',
  },
  ready_to_mail: {
    label: 'Ready to mail',
    chipClass: 'bg-purple-100 text-purple-700',
    icon: 'mail',
    iconClass: 'text-purple-500',
  },
  mailed: {
    label: 'Mailed',
    chipClass: 'bg-green-100 text-green-700',
    icon: 'check',
    iconClass: 'text-green-600',
  },
  completed: {
    label: 'Completed',
    chipClass: 'bg-green-100 text-green-700',
    icon: 'check',
    iconClass: 'text-green-600',
  },
};

// Which benefits program the case applies for. Mirrors the backend's
// resolveBenefitsType: the per-case benefits_program override wins when set,
// otherwise region decides (Ontario → cpp, all others qpp).
export function resolveBenefitsProgram(record: any): 'cpp' | 'qpp' {
  const override = (record?.benefits_program || '').toString().toLowerCase();
  if (override === 'cpp' || override === 'qpp') return override;
  return (record?.region || '').toString().toLowerCase() === 'ontario' ? 'cpp' : 'qpp';
}

// The badge label, with a region tweak: Quebec files QPP directly (doesn't
// "send" to the family), so "Ready to send" reads as "Ready to submit" there.
export function benefitsStateLabel(
  state: BenefitsState,
  benefitsType?: 'cpp' | 'qpp',
): string {
  if (state === 'ready_to_send' && benefitsType === 'qpp') return 'Ready to submit';
  // QPP has no family send: "sent" only ever means the Envoi numérique send to
  // Retraite Québec (T-154).
  if (state === 'sent' && benefitsType === 'qpp') return 'Sent to RRQ';
  return BENEFITS_STATE_DISPLAY[state].label;
}

// Hover text for the agent's note on a manual terminal state.
export function benefitsNoteTitle(state: BenefitsState, note: string): string {
  if (state === 'ineligible') return `Not eligible — ${note}`;
  if (state === 'completed') return `Confirmation # ${note}`;
  return note;
}

// Quick picks for the "Mark ineligible" prompt, taken from the reasons staff
// typed into the confirmation-number field before there was a reason field.
// The free-text field stays the source of truth — a pick just fills it.
export const INELIGIBLE_REASON_PICKS = [
  'Social services paid — not allowed to apply',
  'Family chose not to apply',
  'Never contributed to CPP/QPP',
  'No eligible applicant',
];

// The case-feed line for a 'benefits_status_change' event: `status` is the
// state the agent set, `message` the reason / confirmation number.
export function benefitsTimelineLabel(
  status?: string | null,
  message?: string | null,
): string {
  const note = (message || '').trim();
  if (status === 'ineligible') {
    return note ? `Benefits: Not eligible — ${note}` : 'Benefits: Not eligible';
  }
  if (status === 'completed') {
    return note ? `Benefits: Completed — Confirmation # ${note}` : 'Benefits: Completed';
  }
  return 'Benefits status changed';
}

// Per-form mini status shown in the sidebar breakdown lines. Data-completeness
// and mailing are shared across forms, so a single form only exposes its
// send/sign leg here.
export type FormMiniState = 'not_sent' | 'sent' | 'signed';

// The minimal shape the mini-status needs — the per-form lifecycle endpoint
// (cpp-benefits/status) returns just these three, without the aging timestamps.
export type FormSendSign = { sent: boolean; signed: boolean };

export function formMiniState(f: FormSendSign | undefined): FormMiniState {
  if (!f) return 'not_sent';
  if (f.signed) return 'signed';
  if (f.sent) return 'sent';
  return 'not_sent';
}

export const FORM_MINI_DISPLAY: Record<
  FormMiniState,
  { label: string; chipClass: string }
> = {
  not_sent: { label: 'Not sent', chipClass: 'bg-gray-100 text-gray-500' },
  sent: { label: 'Sent', chipClass: 'bg-amber-100 text-amber-700' },
  signed: { label: 'Signed', chipClass: 'bg-purple-100 text-purple-700' },
};

// --- Front-page list cell helpers -----------------------------------------

// Whole days since an ISO timestamp (never negative). Uses wall-clock now.
export function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

// Compact age label: "today", "3d". >= this many days reads as stale (redden).
export const STALE_DAYS = 7;
export function ageLabel(iso: string | null): string {
  const d = daysSince(iso);
  if (d === null) return '';
  return d === 0 ? 'today' : `${d}d`;
}

// One form's send/sign leg for the two-application front-page view. `dataReady`
// is the shared validation gate (a not-yet-sent form is Ready vs Missing).
export type FormLegKind = 'missing' | 'ready' | 'sent' | 'to_mail';
export interface FormLeg {
  kind: FormLegKind;
  label: string; // full label for the single-line cell
  shortLabel: string; // compact label for the tight two-application view
  age: string; // '' when no date applies
  stale: boolean;
  textClass: string;
}
export function formLeg(form: FormLifecycle, dataReady: boolean): FormLeg {
  if (form.signed) {
    const d = daysSince(form.signedAt);
    return {
      kind: 'to_mail',
      label: 'To mail',
      shortLabel: 'To mail',
      age: ageLabel(form.signedAt),
      stale: d !== null && d >= STALE_DAYS,
      textClass: 'text-purple-700',
    };
  }
  if (form.sent) {
    const d = daysSince(form.sentAt);
    return {
      kind: 'sent',
      label: 'Sent to sign',
      shortLabel: 'Sent',
      age: ageLabel(form.sentAt),
      stale: d !== null && d >= STALE_DAYS,
      textClass: 'text-amber-800',
    };
  }
  return dataReady
    ? { kind: 'ready', label: 'Ready', shortLabel: 'Ready', age: '', stale: false, textClass: 'text-blue-600' }
    : { kind: 'missing', label: 'Missing info', shortLabel: 'Missing', age: '', stale: false, textClass: 'text-red-600' };
}
